import React, { useState, useEffect, useMemo } from "react";
import { useRouter } from "next/router";
import Image from "next/legacy/image";
import Link from "@/utils/link";

import { ChevronRightIcon, ChevronDownIcon } from "@heroicons/react/outline";

import { getMDXComponent } from "mdx-bundler/client";

import { processMdx } from "@/utils/mdx";
import HomeHeader from "@/components/home/HomeHeader";
import ArticleCard from "@/components/article/ArticleCard";
import { fetchGraphql } from "@/utils/query";
import { imageLoader, removeExt } from "@/utils/image";

import Breadcrumbs from "@/components/common/Breadcrumbs";
import SeoLinks from "@/components/home/SeoLinks";
import HomeFooter from "@/components/home/HomeFooter";
import Button from "@/ui/Button";
import { ResourceLink } from "../index";

import HomeSearch from "@/components/home/HomeSearch";

const CustomLink = (props) => {
    const href = props.href;
    const isInternalLink = href && (href.startsWith("/") || href.startsWith("#"));

    if (isInternalLink) {
        return (
            <Link href={href}>
                <a {...props}>{props.children}</a>
            </Link>
        );
    }

    return <a target="_blank" rel="noopener noreferrer" {...props} />;
};

const MDXComponents = {
    Image,
    a: CustomLink,
};

const Guidebook = ({ guidebookId, title, mainImageUrl, sections, articles }) => {
    const router = useRouter();
    const [openAll, setOpenAll] = useState(0);
    const [closeAll, setCloseAll] = useState(0);

    //console.log("guidebook", { guidebookId, title, mainImageUrl, sections });

    return (
        <div className="bg-gray-50">
            <HomeHeader fixed />

            <div className="container mx-auto pt-16 sm:pt-20 pb-4 px-4 sm:px-16">
                <Breadcrumbs
                    links={[
                        { name: "Resources", href: "/resources" },
                        { name: "Guidebooks", href: "/resources/guidebooks" },
                        {
                            name: title,
                            href: "/resources/guidebooks/" + guidebookId,
                            hideMobile: true,
                        },
                    ]}
                />
            </div>

            <div className="relative w-full h-[300px] bg-black">
                {mainImageUrl ? (
                    <Image
                        loader={imageLoader}
                        src={removeExt(mainImageUrl)}
                        alt={title}
                        layout="fill"
                        objectFit="cover"
                        objectPosition="center"
                        priority
                        unoptimized={false}
                        className="opacity-70"
                    />
                ) : null}

                <div className="container mx-auto px-4 sm:px-16">
                    <div className="absolute top-16">
                        <h1 className="text-5xl text-white font-headline font-bold">
                            {title}
                        </h1>

                        <div className="mt-6 sm:mt-8 flex justify-center px-4">
                            <HomeSearch
                                inputClassName="w-[300px] sm:w-[500px]"
                                enableLocation={true}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="pt-8 pb-8 container mx-auto px-4 sm:px-16">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-16">
                    <div className="md:col-span-2 space-y-2">
                        <div className="mb-4 flex space-x-4">
                            <Button
                                color="gray"
                                size="xs"
                                variant="outlined"
                                onClick={() => {
                                    setOpenAll((x) => x + 1);
                                    setCloseAll(0);
                                }}
                            >
                                Open All
                            </Button>
                            <Button
                                color="gray"
                                size="xs"
                                variant="outlined"
                                onClick={() => {
                                    setOpenAll(0);
                                    setCloseAll((x) => x + 1);
                                }}
                            >
                                Close All
                            </Button>
                        </div>

                        {sections.map((section, index) => (
                            <React.Fragment key={index}>
                                <Section
                                    section={section}
                                    openAll={openAll}
                                    closeAll={closeAll}
                                />
                                {(index + 1) % 10 === 0 && index < sections.length - 1 && (
                                    <div className="my-6 py-6 bg-gray-300 px-4">
                                        <HomeSearch
                                            inputClassName="w-[calc(100%-64px)] max-w-[500px]"
                                            enableLocation={true}
                                            higherInput={false}
                                        />
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    <div className="mt-8 sm:mt-0">
                        <h3 className="text-xl text-primary-500 font-headline font-bold">
                            Related Resources
                        </h3>

                        <div className="mt-4 w-full flex flex-col space-y-4 ">
                            <ResourceLink
                                title="Senior Living Guidebooks"
                                href="/resources/guidebooks"
                            />

                            <ResourceLink
                                title="Senior Living Glossary"
                                href="/resources/glossary"
                            />

                            <ResourceLink
                                title="Frequently Asked Questions"
                                href="/resources/faq"
                            />

                            <Link href="/resources">
                                <a className="text-primary-500 text-sm uppercase font-black hover:underline">
                                    See All Resources
                                </a>
                            </Link>
                        </div>

                        <div className="mt-8 sm:flex-shrink-0 sm:w-72">
                            <h3 className="text-xl text-primary-500 font-headline font-bold">
                                Recent Articles
                            </h3>

                            <div className="mt-4 flex flex-col space-y-8">
                                {articles
                                    ? articles.map((a, index) => (
                                          <ArticleCard
                                              key={a ? a.title : index}
                                              article={a}
                                          />
                                      ))
                                    : null}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="z-10">
                <SeoLinks />
                <HomeFooter />
            </div>
        </div>
    );
};

const Section = ({ section, openAll, closeAll }) => {
    const [open, setOpen] = useState(openAll > 0);

    //console.log("all", open, openAll, closeAll);

    useEffect(() => {
        if (openAll > 0) {
            setOpen(true);
        } else if (closeAll > 0) {
            setOpen(false);
        }
    }, [openAll, closeAll]);

    const { code } = section;
    const Component = useMemo(() => getMDXComponent(code), [code]);

    return (
        <div>
            <button
                onClick={() => setOpen((x) => !x)}
                className="w-full flex items-center pl-1 pr-4 py-2 text-left text-primary-500 bg-gray-300 hover:bg-gray-200"
            >
                <div className="h-6">
                    {open ? (
                        <ChevronDownIcon className="w-6 h-6 mr-2" />
                    ) : (
                        <ChevronRightIcon className="w-6 h-6 mr-2" />
                    )}
                </div>
                <span className="font-headline font-bold text-lg">{section.heading}</span>
            </button>

            {open ? (
                <div className="pl-8 pr-4 mb-8 prose max-w-3xl">
                    <Component components={MDXComponents} />
                </div>
            ) : null}
        </div>
    );
};

const SideLink = ({ children, href }) => {
    return (
        <Link href={href}>
            <a className="w-full pt-2 text-md font-bold hover:underline">{children}</a>
        </Link>
    );
};

export const getServerSideProps = async ({ params }) => {
    const result = await fetchGraphql(
        `
        query($find: JSON, $findArticle: JSON, $sortArticle: JSON) {
            guidebooks(find: $find) {
                guidebookId
                title: name
                mainImageUrl
                content                
            }
            articles(find: $findArticle, limit: 3, sort: $sortArticle) {
                articleId
                type
                newsroomType
                title
                date
                description
                mainImageUrl
                keywords
                topic
                author_link {
                    name
                }
            }
        }
    `,
        {
            find: {
                guidebookId: params.guidebookId,
            },
            findArticle: {
                newsroomType: { $ne: "Newsroom" },
            },
            sortArticle: { _updatedAt: -1 },
        }
    );

    let guidebook = null;
    let articles = [];
    if (
        result &&
        result.data &&
        result.data.guidebooks &&
        result.data.guidebooks.length > 0
    ) {
        guidebook = result.data.guidebooks[0];
        articles = result.data.articles;
        //console.log("guidebook", guidebook);
    } else {
        return {
            props: {
                guidebooksId: params.guidebooksId,
                title: "",
                sections: [],
                articles: [],
            },
        };
    }

    let sections = [];
    if (guidebook.content) {
        const f = guidebook.content.split("##");

        for (let i = 1; i < f.length; i++) {
            const lines = f[i].split("\n");
            const heading = lines[0];
            const content = lines.filter((_, index) => index > 0).join("\n");
            const post = await processMdx(
                content.replaceAll("\n", "  \n").replaceAll("<br>", "  \n")
            );
            sections.push({ heading, ...post });
        }
    }

    //console.log("sections", sections);

    return {
        props: {
            guidebookId: params.guidebookId,
            title: guidebook.title,
            mainImageUrl: guidebook.mainImageUrl,
            sections,
            articles,
            meta: {
                title: guidebook.title + " | Purple Door",
                description: "Guidebook on " + guidebook.title + " on Purple Door",
            },
        },
    };
};

export default Guidebook;
